import { useEffect, useState } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { PROFILE_FIELDS, type Me, type UserProfile } from '@kaba/shared';
import { api } from '../lib/api';
import { queryClient } from '../lib/board';
import { toast } from '../lib/toast';
import { FormField, inputCls } from './AdminPage';
import { Avatar, Button, cx } from './ui/ui';

/** Profilbild: quadratisch zugeschnitten und verkleinert auf dem Server; das Original wird nicht gespeichert */
export function AvatarEditor({ me }: { me: Me }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file: File) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.set('file', file);
      const r = await api.post<{ image: string }>('/api/me/avatar', fd);
      queryClient.setQueryData<Me>(['me'], (m) => (m ? { ...m, image: r.image } : m));
      void queryClient.invalidateQueries({ queryKey: ['board'] });
      toast.info('Profilbild gespeichert.');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-4">
      <Avatar user={me} size={80} />
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-2">
          <label className={cx('inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-lg border border-[var(--line)] px-3 text-sm font-medium hover:bg-black/5 sm:min-h-9 dark:hover:bg-white/5', busy && 'opacity-60')}>
            <Camera size={16} aria-hidden />
            {busy ? 'Lädt hoch …' : me.image ? 'Foto ändern' : 'Foto hochladen'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
              className="sr-only"
              aria-label={me.image ? 'Profilbild ändern' : 'Profilbild hochladen'}
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void upload(f);
              }}
            />
          </label>
          {me.image && (
            <Button
              variant="ghost"
              onClick={async () => {
                await api.del('/api/me/avatar');
                queryClient.setQueryData<Me>(['me'], (m) => (m ? { ...m, image: null } : m));
                void queryClient.invalidateQueries({ queryKey: ['board'] });
              }}
            >
              <Trash2 size={15} aria-hidden /> Entfernen
            </Button>
          )}
        </div>
        <p className="text-xs text-[var(--ink-soft)]">JPEG, PNG, WebP oder HEIC (iPhone) bis 10 MB. KaBa schneidet das Foto quadratisch zu und verkleinert es.</p>
      </div>
    </div>
  );
}

/** Freiwillige Angaben für die Zusammenarbeit */
export function ProfileFields({ me }: { me: Me }) {
  const [p, setP] = useState<UserProfile>(me.profile);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => setP(me.profile), [me.profile]);
  const dirty = JSON.stringify(p) !== JSON.stringify(me.profile);
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        // Gleiche Prüfung wie auf dem Server, mit Hinweis am Feld
        const errs: Record<string, string> = {};
        for (const k of ['phone', 'mobile'] as const) if (p[k] && !/^[0-9+()\/.\s-]*$/.test(p[k])) errs[k] = 'Bitte nur Ziffern, Leerzeichen und + ( ) / - verwenden.';
        if (p.website && !/^https?:\/\/\S+$/i.test(p.website.trim())) errs.website = 'Bitte eine Adresse mit https:// angeben.';
        setErrors(errs);
        if (Object.keys(errs).length) {
          document.getElementById(`pf-${Object.keys(errs)[0]}`)?.focus();
          return;
        }
        setBusy(true);
        try {
          const r = await api.put<UserProfile>('/api/me/profile', p);
          queryClient.setQueryData<Me>(['me'], (m) => (m ? { ...m, profile: r } : m));
          void queryClient.invalidateQueries({ queryKey: ['person', me.id] });
          void queryClient.invalidateQueries({ queryKey: ['board'] });
          toast.info('Profil gespeichert.');
        } catch (err) {
          toast.error((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <div>
        <h2 className="text-lg font-semibold">Über mich</h2>
        <p className="text-sm text-[var(--ink-soft)]">Alles freiwillig. Hilft anderen, dich einzuordnen und zu erreichen.</p>
      </div>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        {PROFILE_FIELDS.map((f) => (
          <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : undefined}>
            <FormField label={f.label} htmlFor={`pf-${f.key}`} hint={errors[f.key] ? <span className="text-rose-700 dark:text-rose-300" role="alert">{errors[f.key]}</span> : undefined}>
              {f.type === 'textarea' ? (
                <textarea
                  id={`pf-${f.key}`}
                  className={cx(inputCls, 'min-h-20 py-2')}
                  maxLength={f.max}
                  placeholder={f.placeholder}
                  value={p[f.key]}
                  onChange={(e) => setP({ ...p, [f.key]: e.target.value })}
                />
              ) : (
                <input
                  id={`pf-${f.key}`}
                  className={inputCls}
                  type={f.type ?? 'text'}
                  inputMode={f.type === 'tel' ? 'tel' : undefined}
                  autoComplete={f.autoComplete}
                  maxLength={f.max}
                  placeholder={f.placeholder}
                  aria-invalid={!!errors[f.key] || undefined}
                  value={p[f.key]}
                  onChange={(e) => setP({ ...p, [f.key]: e.target.value })}
                />
              )}
            </FormField>
          </div>
        ))}
      </div>
      <label className="flex min-h-11 items-start gap-3 text-sm">
        <input type="checkbox" className="mt-0.5 size-5 accent-[var(--color-accent)]" checked={p.visible} onChange={(e) => setP({ ...p, visible: e.target.checked })} />
        <span>
          Für Personen sichtbar, mit denen ich ein Board oder Team teile
          <span className="block text-xs text-[var(--ink-soft)]">Ohne Haken sehen andere nur Name, Profilbild und E-Mail-Adresse. Gäste über Gastlinks sehen keine E-Mail-Adresse.</span>
        </span>
      </label>
      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={!dirty || busy}>
          Speichern
        </Button>
      </div>
    </form>
  );
}
